export const Team = {
    RED: 'red',
    BLUE: 'blue'
};

export const defaultSettings = {
    targetScore: 21,
    winBy: 2,
    maxScore: 23,
    redTeamName: 'Red',
    blueTeamName: 'Blue',
    showScoreAnimations: true,
    showGameNotifications: true,
    enableShakeCelebrations: true
};

export const GameNotificationType = {
    NONE: 'none',
    GAME_POINT: 'gamePoint',
    SUDDEN_DEATH: 'suddenDeath',
    DEUCE: 'deuce'
};

export function clampSettings(settings) {
    const targetScore = Math.max(1, settings.targetScore);
    const winBy = Math.max(1, settings.winBy);

    return {
        ...settings,
        targetScore,
        winBy,
        maxScore: Math.max(targetScore + winBy, settings.maxScore),
        showScoreAnimations: settings.showScoreAnimations ?? defaultSettings.showScoreAnimations,
        showGameNotifications: settings.showGameNotifications ?? defaultSettings.showGameNotifications,
        enableShakeCelebrations: settings.enableShakeCelebrations ?? defaultSettings.enableShakeCelebrations
    };
}

export function loadSettings(storage = localStorage) {
    const stored = storage.getItem('AuroraVolleyQuest.GameSettings');
    if (stored) {
        try {
            const settings = JSON.parse(stored);
            return clampSettings(settings);
        } catch (e) {
            return defaultSettings;
        }
    }
    return defaultSettings;
}

export function saveSettings(settings, storage = localStorage) {
    storage.setItem('AuroraVolleyQuest.GameSettings', JSON.stringify(settings));
}

export function isWinningScore(team, redScore, blueScore, settings) {
    const score = team === Team.RED ? redScore : blueScore;
    const otherScore = team === Team.RED ? blueScore : redScore;
    const lead = score - otherScore;

    if (score >= settings.targetScore && lead >= settings.winBy) {
        return true;
    }

    if (score === settings.maxScore && otherScore < settings.maxScore) {
        return true;
    }

    return false;
}

export function getWinner(redScore, blueScore, settings) {
    if (isWinningScore(Team.RED, redScore, blueScore, settings)) {
        return Team.RED;
    }
    if (isWinningScore(Team.BLUE, redScore, blueScore, settings)) {
        return Team.BLUE;
    }
    return null;
}

export function getGameNotification(redScore, blueScore, settings) {
    if (getWinner(redScore, blueScore, settings)) {
        return { type: GameNotificationType.NONE, team: null };
    }

    const redWinsNext = getWinner(redScore + 1, blueScore, settings) === Team.RED;
    const blueWinsNext = getWinner(redScore, blueScore + 1, settings) === Team.BLUE;

    if (redWinsNext && blueWinsNext) {
        return {
            type: GameNotificationType.SUDDEN_DEATH,
            team: null,
            title: 'Next point wins',
            message: 'Max score is in play.'
        };
    }

    if (redWinsNext) {
        return {
            type: GameNotificationType.GAME_POINT,
            team: Team.RED,
            title: `Game point: ${settings.redTeamName}`,
            message: 'Next point can win it.'
        };
    }

    if (blueWinsNext) {
        return {
            type: GameNotificationType.GAME_POINT,
            team: Team.BLUE,
            title: `Game point: ${settings.blueTeamName}`,
            message: 'Next point can win it.'
        };
    }

    const isWinByZone = redScore >= settings.targetScore - 1
        && blueScore >= settings.targetScore - 1
        && Math.abs(redScore - blueScore) < settings.winBy;

    if (isWinByZone) {
        return {
            type: GameNotificationType.DEUCE,
            team: null,
            title: 'Deuce',
            message: `Win by ${settings.winBy}.`
        };
    }

    return { type: GameNotificationType.NONE, team: null };
}
